/**
 * 零 React 依赖的 CSRF fetch 封装。
 * 把 crm 的 403 自动重取令牌（T66 §2.3）泛化成可配置工厂，供模板链五应用复用。
 */

const CSRF_FAILURE_STATUS = 403
const CSRF_FAILURE_MARKER = '请求校验失败'

const DEFAULT_UNAUTHORIZED_EXCLUDE_PATHS = ['/api/auth/me', '/api/auth/local/login']
const DEFAULT_ERROR_MESSAGE = '请求失败，请稍后再试'
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000
type ApiRequestInit = RequestInit & { timeoutMs?: number }

export class ApiClientError extends Error {
  readonly data: Record<string, unknown>
  /** tickets 历史字段名，与 data 指向同一份载荷，避免已发布前端契约分叉。 */
  readonly details: Record<string, unknown>

  readonly requestId: string | undefined
  constructor(message: string, public readonly status: number, data: Record<string, unknown> = {},
    public readonly kind: 'server' | 'network' | 'timeout' | 'http' = 'server', requestId?: string) {
    super(message)
    this.name = 'ApiClientError'
    this.data = data
    this.details = data
    this.requestId = typeof data.requestId === 'string' && data.requestId ? data.requestId : requestId
  }
}

function isCsrfFailure(
  status: number,
  message: string,
  marker = CSRF_FAILURE_MARKER,
): boolean {
  return status === CSRF_FAILURE_STATUS && message.includes(marker)
}

function defaultRedirectToLogin(): void {
  if (typeof window === 'undefined') return
  const redirect = window.location.pathname + window.location.search
  window.location.assign(`/?redirect=${encodeURIComponent(redirect)}`)
}

type CreateApiClientOptions = {
  /** 默认 crm/foodbuy：带当前路径回跳登录页。显式传 null 表示未注册前不跳转（market/tickets/模板/prm）。 */
  onUnauthorized?: (() => void) | null
  /** 401 时不触发跳转/清 token 的精确路径。默认 crm/foodbuy 口径。 */
  unauthorizedExcludePaths?: string[]
  /**
   * 401 排除前缀。tickets/market/模板/prm 现行是 `path.startsWith('/api/auth/')`，
   * 比默认精确名单更宽，调用方按原口径覆盖，不把它们悄悄收窄到 crm 那两条。
   */
  unauthorizedExcludePrefix?: string
  /** 默认 `'请求校验失败'`（crm 后端文案子串）。 */
  csrfFailureMarker?: string
  /** 默认 true（crm/foodbuy）。market/tickets/模板/prm 现行不清 token，传 false。 */
  clearTokenOnUnauthorized?: boolean
  /** CSRF 令牌刷新入口，默认 GET `/api/auth/me`。 */
  csrfRefreshPath?: string
}

type ApiClient = {
  api: <T>(path: string, init?: ApiRequestInit, retriedForCsrf?: boolean) => Promise<T>
  post: <T>(path: string, body?: unknown) => Promise<T>
  put: <T>(path: string, body: unknown) => Promise<T>
  patch: <T>(path: string, body?: unknown) => Promise<T>
  remove: <T>(path: string) => Promise<T>
  upload: <T>(path: string, form: FormData) => Promise<T>
  setCsrfToken: (value: string | null) => void
  getCsrfToken: () => string
  setUnauthorizedHandler: (handler: (() => void) | null) => void
  ApiClientError: typeof ApiClientError
}

function shouldSetJsonContentType(body: BodyInit | null | undefined): boolean {
  return body !== undefined && body !== null && !(body instanceof FormData)
}

function isUnauthorizedExempt(
  path: string,
  exactPaths: string[],
  prefix: string | undefined,
): boolean {
  if (prefix && path.startsWith(prefix)) return true
  return exactPaths.includes(path)
}

function writeFlightKey(method: string, path: string, body: BodyInit | null | undefined): string | null {
  if (body instanceof FormData) return null
  const serialized = typeof body === 'string' ? body : body == null ? '' : String(body)
  return `${method}\0${path}\0${serialized}`
}

const formIdempotencyKeys: string[] = []

/** Form 提交期间把幂等键压栈；api-client 写请求在未显式带头时复用栈顶。 */
export function bindFormIdempotencyKey(key: string | undefined): () => void {
  if (!key) return () => {}
  formIdempotencyKeys.push(key)
  return () => {
    const index = formIdempotencyKeys.lastIndexOf(key)
    if (index >= 0) formIdempotencyKeys.splice(index, 1)
  }
}

function currentFormIdempotencyKey(): string | undefined {
  return formIdempotencyKeys[formIdempotencyKeys.length - 1]
}

async function request(path: string, init: ApiRequestInit) {
  const { timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, signal, ...fetchInit } = init
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => { timedOut = true; controller.abort() }, timeoutMs)
  const combinedSignal = signal ? AbortSignal.any([signal, controller.signal]) : controller.signal
  let onAbort: () => void = () => undefined
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(path, { ...fetchInit, signal: combinedSignal })
        const text = await response.text()
        let body: Record<string, unknown> = {}
        let json = false
        try {
          const value: unknown = JSON.parse(text)
          json = value !== null && typeof value === 'object'
          if (json) body = value as Record<string, unknown>
        } catch { /* 网关 HTML 与空响应由 HTTP 错误路径展示。 */ }
        return { response, body, json }
      })(),
      new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(new ApiClientError(timedOut ? '服务器长时间没有响应' : '网络连接中断', 0, {}, timedOut ? 'timeout' : 'network'))
        combinedSignal.addEventListener('abort', onAbort, { once: true })
        if (combinedSignal.aborted) onAbort()
      }),
    ])
  } catch (error) {
    if (error instanceof ApiClientError) throw error
    throw new ApiClientError(timedOut ? '服务器长时间没有响应' : '网络连接中断', 0, {}, timedOut ? 'timeout' : 'network')
  } finally {
    clearTimeout(timer)
    combinedSignal.removeEventListener('abort', onAbort)
  }
}

export function createApiClient(options: CreateApiClientOptions = {}): ApiClient {
  let csrfToken = ''
  let refreshingCsrf: Promise<void> | null = null
  const inFlightWrites = new Map<string, Promise<unknown>>()
  let unauthorizedHandler: (() => void) | null =
    options.onUnauthorized === undefined ? defaultRedirectToLogin : options.onUnauthorized

  const excludePaths = options.unauthorizedExcludePaths ?? DEFAULT_UNAUTHORIZED_EXCLUDE_PATHS
  const excludePrefix = options.unauthorizedExcludePrefix
  const csrfMarker = options.csrfFailureMarker ?? CSRF_FAILURE_MARKER
  const clearTokenOnUnauthorized = options.clearTokenOnUnauthorized ?? true
  const csrfRefreshPath = options.csrfRefreshPath ?? '/api/auth/me'

  function setCsrfToken(value: string | null) {
    csrfToken = value || ''
  }

  function getCsrfToken() {
    return csrfToken
  }

  function setUnauthorizedHandler(handler: (() => void) | null) {
    unauthorizedHandler = handler
  }

  async function refreshCsrfToken(): Promise<void> {
    if (!refreshingCsrf) {
      refreshingCsrf = request(csrfRefreshPath, { credentials: 'same-origin' })
        .then(({ body }) => {
          csrfToken = typeof body.csrfToken === 'string' ? body.csrfToken : ''
        })
        .catch(() => {
          /* 保持旧值；下一次请求仍会走同样的失败与提示路径 */
        })
        .finally(() => {
          refreshingCsrf = null
        })
    }
    return refreshingCsrf
  }

  async function executeApi<T>(path: string, init: ApiRequestInit = {}, retriedForCsrf = false, reusedIdempotencyKey?: string): Promise<T> {
    const headers = new Headers(init.headers)
    if (shouldSetJsonContentType(init.body)) headers.set('content-type', 'application/json')
    const method = (init.method || 'GET').toUpperCase()
    const isWrite = !['GET', 'HEAD'].includes(method)
    if (csrfToken && isWrite) headers.set('x-csrf-token', csrfToken)
    const isRestWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    const idempotencyKey = isRestWrite
      ? (reusedIdempotencyKey ?? (headers.get('Idempotency-Key') || currentFormIdempotencyKey() || crypto.randomUUID()))
      : undefined
    if (idempotencyKey && !headers.has('Idempotency-Key')) headers.set('Idempotency-Key', idempotencyKey)
    const { response, body, json } = await request(path, { ...init, headers, credentials: 'same-origin' })
    if (!response.ok) {
      const message = typeof body.error === 'string' ? body.error : DEFAULT_ERROR_MESSAGE
      if (
        response.status === 401 &&
        !isUnauthorizedExempt(path, excludePaths, excludePrefix)
      ) {
        if (clearTokenOnUnauthorized) setCsrfToken(null)
        unauthorizedHandler?.()
      }
      if (isWrite && !retriedForCsrf && isCsrfFailure(response.status, message, csrfMarker)) {
        await refreshCsrfToken()
        return executeApi<T>(path, init, true, idempotencyKey)
      }
      if (typeof body.error !== 'string') throw new ApiClientError(message, response.status, body, 'http', response.headers.get('x-request-id') || undefined)
      const headerRequestId = response.headers.get('x-request-id')
      if (!(typeof body.requestId === 'string' && body.requestId) && headerRequestId) body.requestId = headerRequestId
      throw new ApiClientError(message, response.status, body)
    }
    if (response.status === 204 || response.status === 205) return {} as T
    if (!json) throw new ApiClientError(DEFAULT_ERROR_MESSAGE, response.status, body, 'http', response.headers.get('x-request-id') || undefined)
    return body as T
  }

  async function api<T>(path: string, init: ApiRequestInit = {}, retriedForCsrf = false): Promise<T> {
    const method = (init.method || 'GET').toUpperCase()
    const isWrite = !['GET', 'HEAD'].includes(method)
    if (isWrite && !retriedForCsrf) {
      const key = writeFlightKey(method, path, init.body)
      if (key) {
        const existing = inFlightWrites.get(key)
        if (existing) return existing as Promise<T>
        const pending = executeApi<T>(path, init, retriedForCsrf).finally(() => {
          inFlightWrites.delete(key)
        })
        inFlightWrites.set(key, pending)
        return pending
      }
    }
    return executeApi<T>(path, init, retriedForCsrf)
  }

  const post = <T>(path: string, body?: unknown) =>
    api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
  const put = <T>(path: string, body: unknown) =>
    api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
  const patch = <T>(path: string, body?: unknown) =>
    api<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) })
  const remove = <T>(path: string) => api<T>(path, { method: 'DELETE' })
  const upload = <T>(path: string, form: FormData) => api<T>(path, { method: 'POST', body: form })

  return {
    api,
    post,
    put,
    patch,
    remove,
    upload,
    setCsrfToken,
    getCsrfToken,
    setUnauthorizedHandler,
    ApiClientError,
  }
}
